import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, firstValueFrom, Observable, of, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { DatePipe } from '@angular/common';

import { PlanningReportService, ReportGeneratorService, UserManagementService, PropertyListingService, LookupService } from '../../../../../../../app/services/property-matrixV2/services';
import { ExportReportDto, MultiplePropertyListingDto, PropertyListingDto, TaskListDto, UserDto } from '../../../../../../../app/services/property-matrixV2/models';
import { NotificationService } from '../../../../../../../app/shared/services/notification-service/notification.service';
import { ListingStateService } from '../../../../../../shared/services/listing-state-service/listing-state.service';
import { ReportingService } from '../../../../../../../app/shared/services/reporting-service/reporting.service';
import { ReportKeyDialogComponent } from '../report-key-dialog/report-key-dialog.component';

@Component({
  selector: 'app-export-report',
  templateUrl: './export-report.component.html',
  styleUrls: ['./export-report.component.scss', '../../../../../../../css/2-modules/_admin-portal.scss']
})

export class ExportReportComponent implements OnInit, OnChanges {

  @Input() selectedListing: PropertyListingDto;
  @Input() exportReportData: ExportReportDto;
  @Input() listingId: string;
  @Input() multipleListingId: string;
  @Input() isMultiple: boolean;
  @Input() taskNumber: number;
  @Input() listingType: string;
  @Input() selectedListingGroup: MultiplePropertyListingDto;
  @Output() tabNumberEvent = new EventEmitter<number>();
  @Output() hideAddendumsEvent = new EventEmitter<boolean>();

  showLevelOneButton: boolean = false;
  showLevelTwoButton: boolean = false;
  loading: boolean = false;
  canExport: boolean = false;
  taskName: string = "";
  sellerCompanyLogoFile: any;
  sellerCompanyLogoFileId: string | null = null;
  sellerCompanyLogoFileUrl: string | null = null;
  sellerCompanyHideButtons: boolean = false;

  plannerCompanyLogoFile: any;
  plannerCompanyLogoFileId: string | null = null;
  plannerCompanyLogoFileUrl: string | null = null;
  plannerCompanyHideButtons: boolean = false;

  seniorPlannerSignatureFile: any;
  seniorPlannerSignatureFileId: string | null = null;
  seniorPlannerSignatureFileUrl: string | null = null;
  seniorPlannerSignatureHideButtons: boolean = false;

  networkPlannerSignatureFile: any;
  networkPlannerSignatureFileId: string | null = null;
  networkPlannerSignatureFileUrl: string | null = null;
  networkPlannerSignatureHideButtons: boolean = false;
  currentUser: UserDto = {};
  exportReportDto: ExportReportDto = {};
  exportDataForm: FormGroup | null | undefined;
  exportedReportDataFromService: ExportReportDto = {};

  adminPortalAdministratorUserList: UserDto[] = [];
  firstAdminPortalAdministratorUser: UserDto | undefined = undefined;
  seniorPlanneruserSelectedInTask1: UserDto | undefined = undefined;

  sectionsNew = [
    { id: 1, value: 'Project summary', checked: true },
    { id: 2, value: 'Prepared For', checked: false },
    { id: 3, value: 'Prepared By', checked: false },
    { id: 4, value: 'SP Sign Off', checked: false },
    { id: 5, value: 'NP Sign Off', checked: false },
  ];

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private keycloakService: KeycloakService,
    private reportingService: ReportingService,
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private lookupService: LookupService,
    private notificationService: NotificationService,
    private listingStateService: ListingStateService,
    private planningReportService: PlanningReportService,
    private reportGeneratorService: ReportGeneratorService,
    private userManagementService: UserManagementService,
    private propertyListingService: PropertyListingService
  ) { }

  ngOnInit(): void {
    try {
      this.route.queryParams.pipe(take(1)).subscribe(params => {
        this.taskNumber = Number(params['taskNumber']);
        this.taskName = params['taskName'];
        this.listingType = params['listingType'];

      });

      this.getUserId();
      this.loadAllExportReportData();
      this.loadSellerCompanyLogoFile();
      this.loadPlannerCompanyLogoFile();
      this.loadSeniorPlannerSignatureFile();
      this.loadNetworkPlannerSignatureFile();
      this.setFieldToExportData();
      this.getAdminPortalAdministratorUser();
      this.getCurrentUser();
      this.getSeniorPlannerSelected();

      if (this.selectedListing?.allocatePlannerReportSelection == 'Level One Report') {
        this.showLevelOneButton = true;
      } else if (this.selectedListing?.allocatePlannerReportSelection == 'Level Two Report') {
        this.showLevelTwoButton = true;
      } else {
        this.showLevelOneButton = false;
        this.showLevelTwoButton = false;
      }

    } catch (ex) {
      this.notificationService.showErrorMessage("Error", "Could not load screen. Errors found.");
    }
  }

  private getSeniorPlannerSelected() {
    if (this.selectedListing && this.selectedListing?.seniorPlanner) {
      this.seniorPlanneruserSelectedInTask1 = this.selectedListing?.seniorPlanner;
    } else {
      this.notificationService.showErrorMessage("Error", "The current listing senior planner not found. Please notify your administrator.");
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.exportReportData) {
      this.loadAllExportReportData();
    }
  }

  public collapseOrExpandSection(section: { checked: boolean; }) {
    section.checked = !section.checked;
  }

  private loadAllExportReportData() {
    if (this.exportReportData) {
      this.exportReportDto = this.exportReportData || {};
      this.sellerCompanyLogoFileId = this.exportReportData?.sellerCompanyLogoFileId ?? null;
      this.plannerCompanyLogoFileId = this.exportReportData?.plannerCompanyLogoFileId ?? null;
      this.seniorPlannerSignatureFileId = this.exportReportData?.seniorPlannerSignatureFileId ?? null;
      this.networkPlannerSignatureFileId = this.exportReportData?.networkPlannerSignatureFileId ?? null;
      this.checkExportButton()
    } else {
      this.notificationService.showWarningMessage("Warning", "The exportReportData is null");
    }
  }

  public async getExportedData(): Promise<void> {

    this.loading = true;
    this.planningReportService.apiV1PlanningReportGetExportReportDetailsGet({ propertyListingId: this.selectedListing.id })
      .pipe(
        catchError((err) => of(this.notificationService.showErrorMessage("Error", "Could not get the exported report data."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: ExportReportDto | void) => {
        if (result) {
          this.exportedReportDataFromService = result;
        }
      });
  }

  private checkExportButton(): void {
    if (this.exportReportData?.sellerCompanyLogoFileId &&
      this.exportReportData?.projectName &&
      this.exportReportData?.plannerCompanyLogoFileId &&
      this.exportReportData?.seniorPlannerSignatureFileId &&
      this.exportReportData?.networkPlannerSignatureFileId) {

      this.canExport = true;
    } else {
      this.canExport = false;
    }
  }

  private validateExportReport(): boolean {
    try {

      if (!this.exportReportDto.projectName) {
        this.notificationService.showErrorMessage("Error", "The export report project name is required.");
        return false;
      }

      if (!this.exportReportDto.date) {
        this.notificationService.showErrorMessage("Error", "The export report date is required.");
        return false;
      }

      if (!this.exportReportDto.referenceNumber) {
        this.notificationService.showErrorMessage("Error", "The export report reference no is required.");
        return false;
      }

      if (!this.exportReportDto.preparedFor) {
        this.notificationService.showErrorMessage("Error", "The export report prepared for name is required.");
        return false;
      }

      if (!this.exportReportDto.sellerCompany) {
        this.notificationService.showErrorMessage("Error", "The export report prepared for Company is required.");
        return false;
      }

      if (!this.exportReportDto.sellerCompanyLogoFileId) {
        this.notificationService.showErrorMessage("Error", "The export report prepared for Company logo image is required.");
        return false;
      }

      if (!this.exportReportDto.preparedBy) {
        this.notificationService.showErrorMessage("Error", "The export report prepared by name is required.");
        return false;
      }

      if (!this.exportReportDto.plannerCompany) {
        this.notificationService.showErrorMessage("Error", "The export report company of planner is required.");
        return false;
      }

      if (this.taskNumber === 7) {
        if (!this.exportReportDto.networkPlannerName) {
          this.notificationService.showErrorMessage("Error", "The export report network planner name is required.");
          return false;
        }

        if (!this.exportReportDto.networkPlannerSignatureFileId) {
          this.notificationService.showErrorMessage("Error", "The export report network planner signature is required.");
          return false;
        }
      }

      if (this.taskNumber === 8) {
        if (!this.exportReportDto.seniorPlannerName) {
          this.notificationService.showErrorMessage("Error", "The export report senior planner name is required.");
          return false;
        }

        if (!this.exportReportDto.seniorPlannerSignatureFileId) {
          this.notificationService.showErrorMessage("Error", "The export report senior planner signature is required.");
          return false;
        }
      }
    } catch (error) {
    }
    return true;
  }

  public async saveExportReportDetails(): Promise<boolean> {
    if (!this.validateExportReport()) {
      return false;
    }

    try {
      this.loading = true;

      await firstValueFrom(
        this.planningReportService.apiV1PlanningReportAddOrUpdateExportReportPost({
          propertyListingId: this.listingId,
          body: this.exportReportDto
        })
      );
      this.notificationService.showSuccessMessage(
        'Success',
        'Successfully saved export details.'
      );
      return true;
    } catch {
      this.notificationService.showErrorMessage("Error", "Could not save export details.");
      return false;
    } finally {
      this.loading = false;
    }
  }

  public exportLevelOnePlanningReport() {
    this.hideAddendumsEvent.emit(true);
    if (!this.isMultiple) {
      console.log();
      this.exportLevelOneSinglePlanningReport();
    } else {
      console.log();
      this.exportLevelOneMultiplePlanningReport();
    }
    setTimeout(() => {
      this.hideAddendumsEvent.emit(false);
    }, 500);
  }

  private exportLevelOneSinglePlanningReport() {
    this.loading = true;
    this.reportGeneratorService.apiV1ReportGeneratorGenerateLevelOneSingleReportGet({
      listingId: this.listingId,
      userId: this.getUserId()
    }).subscribe({
      next: (_response: any) => {
        this.loading = false;
        this.notificationService.showSuccessMessage('Success', 'Report generation job has been started.');
      },
      error: (_error: any) => {
        this.loading = false;
        this.notificationService.showErrorMessage('Error', 'Could not start report generation.');
      }
    });
  }

  private exportLevelOneMultiplePlanningReport() {
    this.loading = true;
    this.reportGeneratorService.apiV1ReportGeneratorGenerateLevelOneMultipleReportGet({
      multipleListingId: this.multipleListingId,
      userId: this.getUserId()
    }).subscribe({
      next: (_response: any) => {
        this.loading = false;
        this.notificationService.showSuccessMessage('Success', 'Report generation job has been started.');
      },
      error: (_error: any) => {
        this.loading = false;
        this.notificationService.showErrorMessage('Error', 'Could not start report generation.');
      }
    });
  }

  public exportLevelTwoPlanningReport() {
    if (!this.isMultiple) {
      this.exportLevelTwoSinglePlanningReport();
    } else {
      this.exportLevelTwoMultiplePlanningReport();
    }
  }

  private exportLevelTwoSinglePlanningReport() {
    this.loading = true;
    this.reportGeneratorService.apiV1ReportGeneratorGenerateLevelTwoSingleReportGet({
      listingId: this.listingId,
      userId: this.getUserId()
    }).subscribe({
      next: (_response: any) => {
        this.loading = false;
        this.notificationService.showSuccessMessage('Success', 'Report generation job has been started.');
      },
      error: (_error: any) => {
        this.loading = false;
        this.notificationService.showErrorMessage('Error', 'Could not start report generation.');
      }
    });
  }

  private exportLevelTwoMultiplePlanningReport() {
    this.loading = true;
    this.reportGeneratorService.apiV1ReportGeneratorGenerateLevelOneMultipleReportGet({
      multipleListingId: this.multipleListingId,
      userId: this.getUserId()
    }).subscribe({
      next: (_response: any) => {
        this.loading = false;
        this.notificationService.showSuccessMessage('Success', 'Report generation job has been started.');
      },
      error: (_error: any) => {
        this.loading = false;
        this.notificationService.showErrorMessage('Error', 'Could not start report generation.');
      }
    });
  }

  public async setSellerCompanyLogoFile(event: { target: { files: any[]; }; }) {
    try {
      this.sellerCompanyLogoFileId = await this.reportingService.uploadFile(event.target.files[0]);
      this.exportReportData.sellerCompanyLogoFileId = this.sellerCompanyLogoFileId;
      this.sellerCompanyLogoFile = event.target.files[0];

      var reader = new FileReader();

      reader.onload = (event: any) => {
        this.sellerCompanyLogoFileUrl = event.target.result;
        this.sellerCompanyHideButtons = true;
      }

      reader.readAsDataURL(this.sellerCompanyLogoFile);
    }
    catch (error) {
      this.notificationService.showErrorMessage('Error', 'Could not upload file.');
    }
  }

  public replaceSellerCompanyLogoFile() {
    this.sellerCompanyLogoFileUrl = null;
    this.sellerCompanyHideButtons = false;
    this.exportReportData.sellerCompanyLogoFileId = null;
  }

  private async loadSellerCompanyLogoFile() {
    if (this.sellerCompanyLogoFileId) {
      try {
        const fileUrl = await this.reportingService.getFileUrl(this.sellerCompanyLogoFileId);
        this.sellerCompanyLogoFileUrl = fileUrl;
        this.sellerCompanyHideButtons = !!fileUrl;
      } catch (error) {
        this.notificationService.showErrorMessage('Error', 'Could not load file.');
      }
    }
  }

  public async setPlannerCompanyLogoFile(event: { target: { files: any[]; }; }) {
    try {
      this.plannerCompanyLogoFileId = await this.reportingService.uploadFile(event.target.files[0]);
      this.exportReportData.plannerCompanyLogoFileId = this.plannerCompanyLogoFileId;
      this.plannerCompanyLogoFile = event.target.files[0];

      var reader = new FileReader();

      reader.onload = (event: any) => {
        this.plannerCompanyLogoFileUrl = event.target.result;
        this.plannerCompanyHideButtons = true;
      }

      reader.readAsDataURL(this.plannerCompanyLogoFile);
    }
    catch (error) {
      this.notificationService.showErrorMessage('Error', 'Could not upload file.');
    }
  }

  public replacePlannerCompanyLogoFile() {
    this.plannerCompanyLogoFileUrl = null;
    this.plannerCompanyHideButtons = false;
    this.exportReportData.plannerCompanyLogoFileId = null;
  }

  private async loadPlannerCompanyLogoFile() {
    if (this.plannerCompanyLogoFileId) {
      try {
        const fileUrl = await this.reportingService.getFileUrl(this.plannerCompanyLogoFileId);
        this.plannerCompanyLogoFileUrl = fileUrl;
        this.plannerCompanyHideButtons = !!fileUrl;
      } catch (error) {
        this.notificationService.showErrorMessage('Error', 'Could not load file.');
      }
    }
  }

  public async setSeniorPlannerSignatureFile(event: { target: { files: any[]; }; }) {
    try {
      this.seniorPlannerSignatureFileId = await this.reportingService.uploadFile(event.target.files[0]);
      this.exportReportData.seniorPlannerSignatureFileId = this.seniorPlannerSignatureFileId;
      this.seniorPlannerSignatureFile = event.target.files[0];

      var reader = new FileReader();

      reader.onload = (event: any) => {
        this.seniorPlannerSignatureFileUrl = event.target.result;
        this.seniorPlannerSignatureHideButtons = true;
      }

      reader.readAsDataURL(this.seniorPlannerSignatureFile);
    }
    catch (error) {
      this.notificationService.showErrorMessage('Error', 'Could not upload file.');
    }
  }

  public replaceSeniorPlannerSignatureFile() {
    this.seniorPlannerSignatureFileUrl = null;
    this.seniorPlannerSignatureHideButtons = false;
    this.exportReportData.seniorPlannerSignatureFileId = null;
  }

  private async loadSeniorPlannerSignatureFile() {
    if (this.seniorPlannerSignatureFileId) {
      try {
        const fileUrl = await this.reportingService.getFileUrl(this.seniorPlannerSignatureFileId);
        this.seniorPlannerSignatureFileUrl = fileUrl;
        this.seniorPlannerSignatureHideButtons = !!fileUrl;
      } catch (error) {
        this.notificationService.showErrorMessage('Error', 'Could not load file.');
      }
    }
  }

  public async setNetworkPlannerSignatureFile(event: { target: { files: any[]; }; }) {
    try {
      this.networkPlannerSignatureFileId = await this.reportingService.uploadFile(event.target.files[0]);
      this.exportReportData.networkPlannerSignatureFileId = this.networkPlannerSignatureFileId;
      this.networkPlannerSignatureFile = event.target.files[0];

      var reader = new FileReader();

      reader.onload = (event: any) => {
        this.networkPlannerSignatureFileUrl = event.target.result;
        this.networkPlannerSignatureHideButtons = true;
      }

      reader.readAsDataURL(this.networkPlannerSignatureFile);
    }
    catch (error) {
      this.notificationService.showErrorMessage('Error', 'Could not upload file.');
    }
  }

  public replaceNetworkPlannerSignatureFile() {
    this.networkPlannerSignatureFileUrl = null;
    this.networkPlannerSignatureHideButtons = false;
    this.exportReportData.networkPlannerSignatureFileId = null;
  }

  private async loadNetworkPlannerSignatureFile() {
    if (this.networkPlannerSignatureFileId) {
      try {
        const fileUrlNetwork = await this.reportingService.getFileUrl(this.networkPlannerSignatureFileId);
        this.networkPlannerSignatureFileUrl = fileUrlNetwork;
        this.networkPlannerSignatureHideButtons = !!fileUrlNetwork;
      } catch (error) {
        this.notificationService.showErrorMessage('Error', 'Could not load file.');
      }
    }
  }

  private getUserId(): string | undefined {
    return this.keycloakService.getKeycloakInstance().tokenParsed?.sub;
  }

  public openReportKeyDialog(): void {
    this.dialog.open(ReportKeyDialogComponent, {
      width: '30vw',
      height: '35vh'
    });
  }

  public goToAddendumThree() {
    this.tabNumberEvent.emit(4);
  }

  private setFieldToExportData(): void {
    if (this.selectedListing && this.selectedListing.propertyDetail) {
      this.exportReportData.projectName = this.selectedListing.addressTitle;
      let metroAbbreviation = this.getMetroAbbreviation(this.selectedListing.propertyDetail?.riskscape?.riskscapePropertyInfo?.city) ?? "NA";
      let yearMonth = this.datePipe.transform(this.exportReportDto.date, 'yyMM') ?? this.datePipe.transform(new Date(), 'yyMM');
      this.exportReportData.referenceNumber = "LIS/" + metroAbbreviation + "/" + yearMonth + "/001";
    } else {
      this.notificationService.showWarningMessage("Warning", "Could not retrieve exported data pre-populated fields.");
    }
  }

  private getMetroAbbreviation(municipality: string | null | undefined): String {
    let metroAbbreviation = "NA";
    if (municipality) {
      switch (municipality.toLowerCase()) {
        case "city of cape town": {
          metroAbbreviation = "CPT";
          break;
        }
        case "tbc": {
          metroAbbreviation = "TBC";
          break;
        }
        case "city of johannesburg": {
          metroAbbreviation = "JHB";
          break;
        }
        case "ekurhuleni": {
          metroAbbreviation = "EKH";
          break;
        }
        case "city of tshwane": {
          metroAbbreviation = "PTA";
          break;
        }
        case "ethekwini": {
          metroAbbreviation = "ETH";
          break;
        }
        default: {
          break;
        }
      }
    }
    return metroAbbreviation;
  }

  private getCurrentUser(): void {
    this.userManagementService.apiV1UserManagementGetCurrentUserGet().subscribe(result => {
      if (result != null) {
        this.currentUser = result;
      }
      else {
        this.notificationService.showWarningMessage('No Current User', 'No current user found.');
      }
    });
  }

  private navigateToPlanningReport() {
    this.propertyListingService.apiV1PropertyListingGetPropertyListingByIdGet({ listingId: this.selectedListing.id }).subscribe(result => {
      this.selectedListing = result;
      this.listingStateService.setSelectedListing(this.selectedListing);
      this.router.navigate(['admin/listings/listing-overview'], {
        queryParams: {
          listingType: this.listingType
        }
      });
    });
  }

  private validateAllFormFields(formGroup: FormGroup) {
    try {
      Object.keys(formGroup.controls).forEach(field => {
        const control = formGroup.get(field);
        if (control) {
          if (control instanceof FormGroup) {
            this.validateAllFormFields(control);
          } else {
            control.markAsTouched({ onlySelf: true });
          }
        } else {
        }
      });
    }
    catch (e) {
      this.loading = false;
      this.notificationService.showErrorMessage("Error", "Could not save a user, please contact your administrator.");
    }
  }

  public onDateChange(event: any) {
    const formatted = this.datePipe.transform(event, 'yyyy-MM-dd');
    this.exportReportDto.date = formatted ?? '';
  }

  public async completeTask() {
    try {
      await this.saveExportReportDetails();
      await this.updateTaskStatus();
    } catch (error) {
      this.notificationService.showErrorMessage('Error', 'Could not complete task.');
      this.loading = false;
    }
  }


  private async updateTaskStatus(): Promise<void> {
    if (!this.selectedListing?.taskList || !this.currentUser) return;

    const changedTaskLists: TaskListDto[] = [];
    const currentTaskNo = this.taskNumber;
    const nextTaskNo = currentTaskNo + 1;

    const anyInProgress = this.selectedListing.taskList.some(
      task => task.taskStatus?.taskStatusNumber === 2
    );

    this.selectedListing.taskList.forEach(task => {
      const taskNo = task.taskNumber?.number ?? task.taskNumber;
      const statusNo = task.taskStatus?.taskStatusNumber ?? task.taskStatus;

      if (taskNo === currentTaskNo && statusNo !== 3) {
        task.user = this.currentUser;
        changedTaskLists.push(task);
      }

      if (taskNo === nextTaskNo && statusNo === 1 && !anyInProgress) {
        task.user = this.currentUser;
        changedTaskLists.push(task);
      }
    });

    if (changedTaskLists.length > 0) {
      this.loading = true;
      try {
        const result = await this.propertyListingService
          .apiV1PropertyListingUpdatePropertyListingTaskPost({
            body: changedTaskLists,
            currentTaskNumber: currentTaskNo
          })
          .toPromise();

        if (result == null) {
          this.notificationService.showErrorMessage('Error', 'Failed to update planners.');
        } else {
          this.navigateToPlanningReport();
        }
      } catch (error) {
        this.notificationService.showErrorMessage('Error', 'Failed to update planners.');
      } finally {
        this.loading = false;
      }
    } else {
      this.navigateToPlanningReport();
    }
  }

  public async onSaveClick() {
    try {
      this.loading = true;
      const result = await this.saveExportReportDetails();
      this.loading = false;
      if (result === true) {
        this.notificationService.showSuccessMessage(
          'Success',
          'Successfully saved export details.'
        );
      }
    } catch (error) {
      this.loading = false;
    }
  }

  private getAdminPortalAdministratorUser(): void {
    this.loading = true;
    this.lookupService.apiV1LookupGetAdminPortalAdministratorUsersGet({})
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get the admin portal administration user."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: UserDto[] | void) => {
        if (result && result.length > 0) {
          this.adminPortalAdministratorUserList = result;
          this.firstAdminPortalAdministratorUser = this.adminPortalAdministratorUserList[0] as UserDto;
        } else {
          this.notificationService.showWarningMessage("Warning", "There are no users found with a Admin portal administrator role.Please add user with that role.");
        }
      });
  }
}
